// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { InvokeAskRequest, InvokeAskResponse } from '../types';

// ============= Constants =============
const ASK_BASE_URL = import.meta.env.VITE_BASE_ASK_URL;
const ASK_API_ENDPOINT = '/api/Ask';
const ASK_LITIUM_SCOPE =
  'api://c98b7623-f120-463e-9a81-42e287c0ab58/access_as_user';

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface InvokeAskProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  askSessionId: string;
  data: InvokeAskRequest;
  /** When aborted, the fetch rejects with `AbortError`. */
  signal?: AbortSignal;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Invokes the Ask to get a response from bot or application depending on the Ask
 * @param getAccessToken Function to acquire access token
 * @param askSessionId Ask Session identifier
 * @param data Request body for invoking Ask
 * @returns Promise with API response containing Ask response data or error
 */
export const invokeAsk = async ({
  getAccessToken,
  askSessionId,
  data,
  signal,
}: InvokeAskProps): Promise<InvokeAskResponse> => {
  try {
    console.log('🔄 Invoking Ask...');

    // Token acquisition with required scopes
    const token = await getAccessToken([ASK_LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${ASK_BASE_URL}${ASK_API_ENDPOINT}/${askSessionId}/ask`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify(data),
      signal,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Ask invoked successfully');

    return result;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    console.error('🔴 Error invoking Ask:', error);
    throw error;
  }
};
